import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Theme,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export type SearchableSelectOption = {
  value: string;
  label: string;
};

export type SearchableSelectMAIQProps = {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  displayEmpty?: boolean;
  emptyLabel?: string;
  searchPlaceholder?: string;
  emptySearchLabel?: string;
  /** Sort options by label (case-insensitive). Default true. */
  sortOptions?: boolean;
  maxWidth?: number | string;
  menuMaxHeight?: number;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  formControlSx?: SxProps<Theme>;
};

/**
 * Single-select MUI Select with sticky in-menu search (Content Management timezone pattern).
 */
export const SearchableSelectMAIQ: React.FC<SearchableSelectMAIQProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  displayEmpty = true,
  emptyLabel = 'Select…',
  searchPlaceholder = 'Search…',
  emptySearchLabel = 'No options found',
  sortOptions = true,
  maxWidth = 400,
  menuMaxHeight = 300,
  size = 'small',
  sx,
  formControlSx,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sortedOptions = useMemo(() => {
    if (!sortOptions) return options;
    return [...options].sort((a, b) =>
      (a.label || '').localeCompare(b.label || '', undefined, {
        sensitivity: 'base',
      }),
    );
  }, [options, sortOptions]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedOptions;
    return sortedOptions.filter(option =>
      (option.label || '').toLowerCase().includes(term),
    );
  }, [sortedOptions, searchTerm]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as string);
  };

  return (
    <FormControl
      fullWidth
      size={size}
      sx={{ maxWidth, ...formControlSx }}
      disabled={disabled}
    >
      <Select
        value={value}
        onChange={handleChange}
        open={open}
        onOpen={() => {
          setOpen(true);
          setSearchTerm('');
        }}
        onClose={() => {
          setOpen(false);
          setSearchTerm('');
        }}
        displayEmpty={displayEmpty}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '8px 14px',
          },
          ...sx,
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: menuMaxHeight,
            },
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
          }}
          onClick={e => e.stopPropagation()}
        >
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          />
        </Box>
        {displayEmpty ? (
          <MenuItem value="">
            <em>{emptyLabel}</em>
          </MenuItem>
        ) : null}
        {filteredOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        {filteredOptions.length === 0 && searchTerm ? (
          <MenuItem disabled>{emptySearchLabel}</MenuItem>
        ) : null}
      </Select>
    </FormControl>
  );
};

export default SearchableSelectMAIQ;
